<script setup lang="ts">
type StudentUserData = App.Auth.Data.StudentUserData;

import Alert from '@/components/alert/Alert.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';
import Dialog from '@/components/dialog/Dialog.vue';
import FormRows from '@/components/formRows/FormRows.vue';
import FormTextInput from '@/components/formTextInput/FormTextInput.vue';

import Check from '@/icons/line/check.svg';
import InfoCircle from '@/icons/line/info-circle.svg';
import Phone from '@/icons/line/phone.svg';

const props = defineProps<{
  user: StudentUserData;
}>();

const { show, close, closeLocally } = useDialog();
const [isCancelDialogOpen, toggleCancelDialog] = useToggle(false);
const [isSecuritySettingsDialogOpen, toggleSecuritySettingsDialog] = useToggle(false);

const isDialogOpen = computed(
  () => !isCancelDialogOpen.value && !isSecuritySettingsDialogOpen.value && show.value
);

const form = useForm({
  url: route('student.profile.phone-numbers.update'),
  method: 'PUT',
  fields: {
    cell_phone: props.user.cell_phone,
    home_phone: props.user.home_phone
  },
  hooks: {
    success() {
      close();
    }
  }
});

function handleFormSubmit() {
  form.submit();
}

function handleToggleCancelDialog() {
  if (form.isDirty) {
    toggleCancelDialog();
    return;
  }
  closeLocally();
}

function handleCancelDialogConfirm() {
  toggleCancelDialog();
  closeLocally();
}

function handleNavigateToSecuritySettings() {
  router.navigate({ url: route('student.profile.security.show') });
}

function handleSecuritySettingsClick() {
  if (form.isDirty) {
    toggleSecuritySettingsDialog();
    return;
  }
  handleNavigateToSecuritySettings();
}

function handleSecuritySettingsDialogConfirm() {
  form.submitWith({
    hooks: {
      success() {
        close();
        handleNavigateToSecuritySettings();
      },
      after() {
        toggleSecuritySettingsDialog();
      }
    }
  });
}

function handleSecuritySettingsDialogCancel() {
  toggleSecuritySettingsDialog();
  handleNavigateToSecuritySettings();
}
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/student-parent.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <Dialog
    title="Phone"
    :confirmButtonIcon="Check"
    :cancelButtonDisabled="!form.isDirty"
    :isLoading="form.processing"
    :isOpen="isDialogOpen"
    cancelButtonLabel="Reset"
    confirmButtonLabel="Save changes"
    @onCancel="form.reset"
    @onClose="handleToggleCancelDialog"
    @onConfirm="handleFormSubmit"
  >
    <template #form>
      <FormRows>
        <FormTextInput
          :error="form.errors.cell_phone"
          :iconLeft="Phone"
          :modelValue="form.fields.cell_phone"
          isOptional
          label="Cell phone"
          name="cell_phone"
          type="tel"
          @update:modelValue="(value) => (form.fields.cell_phone = value)"
        />
        <FormTextInput
          :error="form.errors.home_phone"
          :iconLeft="Phone"
          :modelValue="form.fields.home_phone"
          isOptional
          label="Home phone"
          name="home_phone"
          type="tel"
          @update:modelValue="(value) => (form.fields.home_phone = value)"
        />
        <Alert
          :icon="InfoCircle"
          color="secondary"
          title="Looking to edit your 2FA phone number?"
          variant="soft"
          hideClose
        >
          <p>
            Go to
            <button
              type="button"
              class="font-inherit leading-inherit cursor-pointer border-none bg-transparent p-0 text-inherit underline hover:no-underline"
              @click="handleSecuritySettingsClick"
            >
              Password & 2FA settings</button
            >.
          </p>
        </Alert>
      </FormRows>
    </template>
  </Dialog>
  <DangerDialog
    title="Discard Changes?"
    message="Your changes will not be saved."
    :isOpen="isCancelDialogOpen"
    cancelButtonLabel="Stay on this page"
    confirmButtonLabel="Discard changes"
    isStacked
    @onCancel="toggleCancelDialog()"
    @onClose="toggleCancelDialog()"
    @onConfirm="handleCancelDialogConfirm"
  />
  <Dialog
    title="Save Changes First?"
    message="You have unsaved changes. Would you like to save them before going to security settings?"
    :isOpen="isSecuritySettingsDialogOpen"
    :isLoading="form.processing"
    :confirmButtonIcon="Check"
    confirmButtonLabel="Save and continue"
    cancelButtonLabel="Continue without saving"
    isStacked
    @onCancel="() => handleSecuritySettingsDialogCancel()"
    @onClose="() => toggleSecuritySettingsDialog()"
    @onConfirm="handleSecuritySettingsDialogConfirm"
  />
</template>
